import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { 
  ArrowLeft, 
  LogOut, 
  User, 
  MapPin, 
  Calendar, 
  FileText, 
  Phone, 
  Mail,
  Download,
  Building,
  Clock,
  Eye,
  Archive
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AdminArchivedOfferDetailPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { offerId } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOfferDetails();
  }, [offerId]);

  const fetchOfferDetails = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/${offerId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Vérifier que l'offre appartient à la société de l'admin
        if (data.company !== user?.company) {
          toast.error('Accès non autorisé à cette offre');
          navigate('/admin');
          return;
        }
        setOffer(data);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/admin');
      }
    } catch (error) {
      console.error('Error fetching offer:', error);
      toast.error('Erreur de connexion');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'accepte':
        return 'admin-status-badge-accepte';
      case 'refuse':
        return 'admin-status-badge-refuse';
      case 'en_attente':
        return 'admin-status-badge-en-attente';
      default:
        return 'admin-status-badge-default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepte':
        return 'ACCEPTÉ';
      case 'refuse':
        return 'REFUSÉ';
      case 'en_attente':
        return 'EN ATTENTE';
      default:
        return status?.toUpperCase() || 'N/A';
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!offer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-destructive mb-4">Offre non trouvée</h2>
          <Button onClick={() => navigate('/admin')}>Retour</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-offer-detail-page">
      {/* Header */}
      <header className="admin-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/admin/offres-archivees')}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="admin-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="admin-header-center">
          <h1>Détail de l'offre archivée - {user?.company}</h1>
          <p className="admin-header-subtitle">Consultation détaillée (lecture seule)</p>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-detail-container">
          {/* Archive Banner */}
          <div className="admin-archive-banner">
            <Archive className="w-5 h-5" />
            <span>Cette offre est archivée et en lecture seule - Archivée le {formatDate(offer.updated_at)}</span>
          </div>

          {/* Offer Details - Version compacte */}
          <Card className="admin-offer-detail-card">
            <CardContent className="admin-offer-detail-content-compact">
              <div className="admin-offer-detail-header-compact">
                <h2 className="admin-offer-detail-title-compact">{offer.company} : {offer.title}</h2>
                <div className="admin-offer-badge admin-offer-badge-archive">
                  <Archive className="w-4 h-4 mr-1" />
                  ARCHIVÉE
                </div>
              </div>

              <div className="admin-offer-detail-grid-compact">
                <div className="admin-detail-section-compact">
                  <div className="admin-detail-items-compact">
                    <div className="admin-detail-item-compact">
                      <div className="admin-detail-label-compact">
                        <User className="w-4 h-4" />
                        <span>Manager: {offer.created_by_name || 'N/A'}</span>
                      </div>
                    </div>

                    <div className="admin-detail-item-compact">
                      <div className="admin-detail-label-compact">
                        <Calendar className="w-4 h-4" />
                        <span>Créée le {formatDate(offer.created_at)}</span>
                      </div>
                    </div>

                    <div className="admin-detail-item-compact">
                      <div className="admin-detail-label-compact">
                        <Calendar className="w-4 h-4" />
                        <span>Archivée le {formatDate(offer.updated_at)}</span>
                      </div>
                    </div>

                    {offer.lieu_travail && (
                      <div className="admin-detail-item-compact">
                        <div className="admin-detail-label-compact">
                          <MapPin className="w-4 h-4" />
                          <span>Lieu: {offer.lieu_travail}</span>
                        </div>
                      </div>
                    )}

                    {offer.date_arrivee && (
                      <div className="admin-detail-item-compact">
                        <div className="admin-detail-label-compact">
                          <Calendar className="w-4 h-4" />
                          <span>Arrivée prévue: {formatDate(offer.date_arrivee)}</span>
                        </div>
                      </div>
                    )}

                    {offer.type_poste && (
                      <div className="admin-detail-item-compact">
                        <div className="admin-detail-label-compact">
                          <Clock className="w-4 h-4" />
                          <span>Type: {offer.type_poste}</span>
                        </div>
                      </div>
                    )}

                    {offer.taux_horaire && (
                      <div className="admin-detail-item-compact">
                        <div className="admin-detail-label-compact">
                          <span>€</span>
                          <span>Taux: {offer.taux_horaire}€/h</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Description compacte */}
                {offer.description && (
                  <div className="admin-detail-section-compact">
                    <div className="admin-description-content-compact">
                      <strong>Description:</strong> {offer.description}
                    </div>
                  </div>
                )}

                {/* Qualités attendues compactes */}
                {offer.qualites_attendues && (
                  <div className="admin-detail-section-compact">
                    <div className="admin-description-content-compact">
                      <strong>Qualités attendues:</strong> {offer.qualites_attendues}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Candidatures Table */}
          <Card className="admin-candidatures-card">
            <CardContent className="admin-candidatures-content">
              <div className="admin-candidatures-header">
                <h3 className="admin-section-title">
                  Candidatures ({offer.candidatures ? offer.candidatures.length : 0})
                </h3>
              </div>

              {offer.candidatures && offer.candidatures.length > 0 ? (
                <div className="admin-candidatures-table">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Nom</th>
                        <th>Documents (CV & Lettre)</th>
                        <th>Contact</th>
                        <th>Commentaire</th>
                        <th>Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {offer.candidatures.map((candidature) => (
                        <tr key={candidature.id}>
                          <td className="admin-candidature-name">
                            {candidature.prenom} {candidature.nom}
                          </td>
                          <td className="admin-candidature-documents">
                            <div className="admin-document-buttons">
                              <span className="admin-document-readonly">
                                {candidature.cv_filename ? 'CV disponible' : 'CV indisponible'}
                              </span>
                              <span className="admin-document-readonly">
                                {candidature.lettre_filename ? 'LM disponible' : 'LM indisponible'}
                              </span>
                            </div>
                            <p className="admin-document-note">Lecture seule</p>
                          </td>
                          <td className="admin-candidature-contact">
                            {candidature.email && (
                              <div className="admin-contact-item">
                                <Mail className="w-4 h-4" />
                                <span>{candidature.email}</span>
                              </div>
                            )}
                            {candidature.telephone && (
                              <div className="admin-contact-item">
                                <Phone className="w-4 h-4" />
                                <span>{candidature.telephone}</span>
                              </div>
                            )}
                          </td>
                          <td className="admin-candidature-comment">
                            {candidature.commentaire || 'Aucun commentaire'}
                          </td>
                          <td className="admin-candidature-status">
                            <div className={`admin-status-badge ${getStatusBadgeClass(candidature.status)}`}>
                              {getStatusText(candidature.status)}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="admin-empty-candidatures">
                  <p>Aucune candidature pour cette offre</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminArchivedOfferDetailPage;